import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Typography, Button, Space, Spin } from 'antd';
import { MessageOutlined, UserOutlined, MedicineBoxOutlined, LogoutOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatUserDisplayName } from '../utils/auth';

const { Title, Text } = Typography;

const MainPage: React.FC = () => {
  const navigate = useNavigate();
  const { authState, logout } = useAuth();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // 监听屏幕尺寸变化
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 检查用户是否已登录，未登录则跳转到登录页（只有在loading完成后才检查）
  useEffect(() => {
    if (!authState.isLoading && !authState.isAuthenticated) {
      navigate('/login');
    }
  }, [authState.isLoading, authState.isAuthenticated, navigate]);

  const handleCardClick = (path: string) => {
    navigate(path);
  };

  const handleLogoutClick = () => {
    logout();
  };

  // 如果正在加载，显示loading状态
  if (authState.isLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  // 如果未登录，返回null（因为会跳转到登录页）
  if (!authState.isAuthenticated) {
    return null;
  }

     // 根据用户角色渲染不同的卡片
   const renderServiceCards = () => {
     if (authState.user?.is_doctor) {
      // 医生用户只显示医生端
      return (
        <Col xs={24} sm={24} md={12} lg={8} style={{ display: 'flex', justifyContent: 'center' }}>
          <Card
            hoverable
            style={{
              height: '280px',
              borderRadius: '16px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.3s ease'
            }}
            bodyStyle={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: '32px'
            }}
            onClick={() => handleCardClick('/doctor')}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-8px)';
              e.currentTarget.style.boxShadow = '0 12px 48px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.1)';
            }}
          >
            <div style={{
              backgroundColor: '#fff2e8',
              borderRadius: '50%',
              width: '80px',
              height: '80px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '24px'
            }}>
              <MedicineBoxOutlined style={{ fontSize: '40px', color: '#fa8c16' }} />
            </div>
            <Title level={3} style={{ marginBottom: '12px' }}>
              医生端
            </Title>
            <Text style={{ color: '#666', fontSize: '16px', lineHeight: '1.6' }}>
              查看用户报告和管理诊断记录
            </Text>
          </Card>
        </Col>
             );
     } else {
       // 普通用户显示QA和预问诊
      return (
        <>
          <Col xs={24} sm={24} md={12}>
            <Card
              hoverable
              style={{
                height: '280px',
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              bodyStyle={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '32px'
              }}
              onClick={() => handleCardClick('/qa')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 48px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.1)';
              }}
            >
              <div style={{
                backgroundColor: '#e6f7ff',
                borderRadius: '50%',
                width: '80px',
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px'
              }}>
                <MessageOutlined style={{ fontSize: '40px', color: '#1890ff' }} />
              </div>
              <Title level={3} style={{ marginBottom: '12px' }}>
                AI智能问答
              </Title>
              <Text style={{ color: '#666', fontSize: '16px', lineHeight: '1.6' }}>
                智能问答服务，快速获取医疗健康相关问题的专业解答
              </Text>
            </Card>
          </Col>

          <Col xs={24} sm={24} md={12}>
            <Card
              hoverable
              style={{
                height: '280px',
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.3s ease'
              }}
              bodyStyle={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                padding: '32px'
              }}
              onClick={() => handleCardClick('/user')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-8px)';
                e.currentTarget.style.boxShadow = '0 12px 48px rgba(0, 0, 0, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 32px rgba(0, 0, 0, 0.1)';
              }}
            >
              <div style={{
                backgroundColor: '#f6ffed',
                borderRadius: '50%',
                width: '80px',
                height: '80px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: '24px'
              }}>
                <UserOutlined style={{ fontSize: '40px', color: '#52c41a' }} />
              </div>
              <Title level={3} style={{ marginBottom: '12px' }}>
                预问诊
              </Title>
              <Text style={{ color: '#666', fontSize: '16px', lineHeight: '1.6' }}>
                个性化预问诊服务，根据您的症状提供特定预问诊调查
              </Text>
            </Card>
          </Col>
        </>
      );
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center'
    }}>
      {/* 顶部导航栏 */}
      <div style={{
        width: '100%',
        maxWidth: '1200px',
        marginBottom: '60px'
      }}>
        {isMobile ? (
          // 手机模式下的布局
          <div>
            {/* 只显示用户信息和退出按钮 */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Text style={{ color: 'white', fontSize: '13px' }}>
                欢迎，{authState.user ? formatUserDisplayName(authState.user) : '用户'}
              </Text>
              <Button 
                type="default" 
                icon={<LogoutOutlined />} 
                onClick={handleLogoutClick}
                size="small"
              >
                退出登录
              </Button>
            </div>
          </div>
        ) : (
          // 桌面模式下的布局
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <img 
                src="./dakanglogo.png" 
                alt="logo" 
                style={{ width: '40px', height: '40px' }}
              />
              <Title level={2} style={{ color: 'white', margin: 0 }}>
                达康AI医疗辅助系统
              </Title>
            </div>
            <Space>
              <Text style={{ color: 'white', marginRight: '8px' }}>
                欢迎，{authState.user ? formatUserDisplayName(authState.user) : '用户'}
              </Text>
              <Button 
                type="default" 
                icon={<LogoutOutlined />} 
                onClick={handleLogoutClick}
              >
                退出登录
              </Button>
            </Space>
          </div>
        )}
      </div>

             {/* 主标题 */}
       <div style={{ textAlign: 'center', marginBottom: '60px' }}>
         <Title level={1} style={{ color: 'white', marginBottom: '16px', fontSize: isMobile ? '24px' : undefined }}>
           {authState.user?.is_doctor 
             ? '达康AI医疗辅助系统 - 医生工作台' 
             : '欢迎使用达康AI医疗辅助系统'
           }
         </Title>
         <Text style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: isMobile ? '16px' : '18px' }}>
           {authState.user?.is_doctor 
             ? '管理患者诊断记录，查看用户健康报告' 
             : '选择您需要的服务，开始您的健康之旅'
           }
         </Text>
       </div>

      {/* 卡片区域 */}
      <Row gutter={[32, 32]} style={{ width: '100%', maxWidth: '1000px', justifyContent: 'center' }}>
        {renderServiceCards()}
      </Row>

      {/* 底部信息 */}
      <div style={{ marginTop: '80px', textAlign: 'center' }}>
        <Text style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '14px' }}>
          © 2025 达康AI医疗辅助系统（试运行版）
        </Text>
      </div>
    </div>
  );
};

export default MainPage;
